import { createTheme } from "@mui/material/styles";

/* ------------------------------------------------------------------ *
 * Mode-aware surfaces and chart chrome
 *
 * MUI's grey.50 / grey.100 do not flip with palette.mode — they are the
 * same near-white in both. Used as a card background they leave dark mode
 * with a white panel carrying the theme's light text on it, so the
 * readouts inside become unreadable. The same applies to the slate hexes
 * (#e2e8f0, #cbd5e1, #64748b) that chart gridlines and axes were drawn
 * with: fine on paper, glaring or invisible on a dark surface.
 *
 * Anything that needs to sit a step above its parent surface, or to draw
 * chart chrome, should take these instead of a baked value.
 * ------------------------------------------------------------------ */

/* Small read-out cards that sit inside a panel: a faint grey wash in
   light mode, a faint white wash in dark, so the lift reads the same way
   round in both. */
export const previewSurfaceSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  bgcolor: (t) =>
    t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : t.palette.grey[50],
};

/* The canvas a diagram is drawn on — one step flatter than a read-out
   card, since the drawing itself supplies the contrast. */
export const diagramSurfaceSx = {
  bgcolor: (t) =>
    t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#FBFCFE",
};

/* A field that is filled by the model rather than the user. It should
   look inert in both modes without going white on dark. */
export const readOnlyFieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: (t) =>
      t.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : t.palette.grey[100],
  },
  "& .MuiOutlinedInput-input": { cursor: "default" },
};

/* Line colours for the preview charts. `main` is what the run actually
   uses, `base` the raw curve behind it. Dark mode takes the primary
   green so the charts read as part of the same UI as everything else. */
export const getPreviewAccents = (mode) =>
  mode === "dark"
    ? { base: "#8FA9BC", main: "#4CAF84" }
    : { base: "#8CA3B8", main: "#1D5FBF" };

/* Gridlines, axis lines and tick labels — the furniture around the data,
   which should recede in both modes rather than compete with it. */
export const getChartChrome = (mode) =>
  mode === "dark"
    ? {
        grid: "rgba(255,255,255,0.12)",
        axisLine: "rgba(255,255,255,0.24)",
        tick: "#8FAF9C",
        tickFaint: "#7B9A88",
        alert: "#FFB74D",
        datasheet: "#4DD0B1",
        inactiveBar: "rgba(255,255,255,0.14)",
        neutralSeries: "#B8C7D4",
        // Recharts renders its tooltip as a white card with dark text by
        // default. Unstyled it reads as a light-mode artefact dropped onto a
        // dark page, and the faint series labels become almost invisible
        // against it. These three are passed to <Tooltip contentStyle /> etc.
        tooltipBg: "#12211A",
        tooltipBorder: "rgba(255,255,255,0.18)",
        tooltipText: "#E8F3EE",
        tooltipLabel: "#8FAF9C",
      }
    : {
        grid: "#e2e8f0",
        axisLine: "#cbd5e1",
        tick: "#64748b",
        tickFaint: "#94a3b8",
        alert: "#B45309",
        datasheet: "#0f766e",
        inactiveBar: "#e2e8f0",
        neutralSeries: "#334155",
        // Light mode already looked right; these keep the two branches
        // symmetrical so a chart never has to test the mode itself.
        tooltipBg: "#ffffff",
        tooltipBorder: "#cbd5e1",
        tooltipText: "#1e293b",
        tooltipLabel: "#64748b",
      };

/* Single-line-diagram palette. `ink` is the conductor/glyph stroke,
   `muted` the annotation text, and the two fills are the asset and grid
   boxes. */
export const getSchematicColors = (mode) =>
  mode === "dark"
    ? {
        ink: "#7FD1A8",
        muted: "#A7CDB5",
        assetFill: "rgba(255,255,255,0.07)",
        assetText: "#EAF7EE",
        gridFill: "rgba(76,175,132,0.16)",
      }
    : {
        ink: "#1F4E79",
        muted: "#64748B",
        assetFill: "#F1F5F9",
        assetText: "#334155",
        gridFill: "#EAF1F8",
      };

/* Recharts <Tooltip> panel, themed for dark mode only. Recharts' default is a
   white panel whose label color is inherited from the page body - correct in
   light mode, unreadable in dark (the body text is near-white, so the label
   vanishes on the white panel). Spread the returned props onto a
   <RechartsTooltip>; in light mode this returns {} so nothing changes there.
   Series value colors are left alone (identity colors stay fixed in both
   modes, per the token convention above). */
export const getRechartsTooltipProps = (mode) => {
  if (mode !== "dark") return {};
  const chrome = getChartChrome("dark");
  return {
    contentStyle: {
      backgroundColor: chrome.tooltipBg,
      border: `1px solid ${chrome.tooltipBorder}`,
      borderRadius: 8,
      color: chrome.tooltipText,
      boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
    },
    labelStyle: { color: chrome.tooltipLabel, fontWeight: 600 },
    itemStyle: { color: chrome.tooltipText },
  };
};

export const getTheme = (darkMode) =>
  createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",

      primary: {
        main: darkMode ? "#4CAF84" : "#0B4DA2",
      },

      background: {
        // Page background
        default: darkMode ? "#0E1A14" : "#f8fbfaff",

        // Cards, panels, accordions, paper
        paper: darkMode ? "#161F1B" : "#f8fbfaff",
      },

      divider: darkMode
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.08)",

      action: {
        hover: darkMode
          ? "rgba(76,175,132,0.08)"
          : "rgba(11,77,162,0.06)",

        selected: darkMode
          ? "rgba(76,175,132,0.18)"
          : "rgba(11,77,162,0.12)",
      },

      text: {
        primary: darkMode ? "#EAF7EE" : "#102A43",
        secondary: darkMode ? "#A7CDB5" : "#486581",
      },
    },

    shape: {
      borderRadius: 10,
    },

    // A scale, where before there was only a face. With nothing defined here,
    // every component chose its own size: 451 ad-hoc fontSize values across
    // src/, more than twenty distinct ones, nine of them between 10 and 13px.
    // Section titles were subtitle2 at 700 -- 14px, the same as the body they
    // headed -- so a heading and an emphasised sentence were told apart by
    // weight alone, and with 700 used 655 times, weight told nothing.
    //
    // The steps below are the ones a page actually needs: title, card, section,
    // body, note. body2 stays at 14 because that is what the whole app is set
    // in, and moving it would move everything. caption goes from 12 to 12.5 and
    // leans on colour for its rank rather than on being small. Poppins has a
    // tall x-height, so these run a little under the Inter equivalents.
    typography: {
      fontFamily: "'Poppins', 'Inter', sans-serif",
      h4: { fontSize: 28, fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.01em" },
      h5: { fontSize: 22, fontWeight: 700, lineHeight: 1.25 },
      h6: { fontSize: 18, fontWeight: 700, lineHeight: 1.3 },
      // The section title: one clear step above the body it heads.
      subtitle1: { fontSize: 16, fontWeight: 600, lineHeight: 1.35 },
      // A label or a row heading: body size, but set apart by weight only.
      subtitle2: { fontSize: 14, fontWeight: 600, lineHeight: 1.4 },
      body1: { fontSize: 15, fontWeight: 400, lineHeight: 1.5 },
      body2: { fontSize: 14, fontWeight: 400, lineHeight: 1.45 },
      caption: { fontSize: 12.5, fontWeight: 400, lineHeight: 1.4 },
      overline: { fontSize: 11, fontWeight: 600, lineHeight: 1.4, letterSpacing: "0.06em" },
      button: { fontSize: 14, fontWeight: 600, lineHeight: 1.4, letterSpacing: "0.01em" },
    },

    components: {
      MuiButton: {
        styleOverrides: {
          root: { fontFamily: "inherit", borderRadius: 8 },
          sizeSmall: { fontSize: 14, minHeight: 32, padding: "4px 10px" },
          sizeMedium: { fontSize: 14, minHeight: 36, padding: "6px 14px" },
          sizeLarge: { fontSize: 14, minHeight: 44, padding: "10px 18px" },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          /* Chrome/Edge autofill paints its own baked-in light-blue fill that
             ignores the theme. In dark mode, repaint it with an opaque inset
             shadow in palette colors; light mode keeps the native look (it
             already reads fine there). */
          ...(darkMode
            ? {
                "input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus":
                  {
                    WebkitBoxShadow: "0 0 0 100px #1E2A24 inset",
                    WebkitTextFillColor: "#EAF7EE",
                    caretColor: "#EAF7EE",
                    borderRadius: "inherit",
                    transition: "background-color 5000s ease-in-out 0s",
                  },
              }
            : {}),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none", // kill gray gradients
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { height: 6, borderRadius: 3 },
          bar: { borderRadius: 3 },
        },
      },

      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: "none",
          },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            "&.Mui-selected": {
              fontWeight: 600,
            },
          },
        },
      },
      // MUI's default tab is upper-case, and upper-case at 14px shouts louder
      // than a 16px section title in mixed case. The tabs were the loudest
      // thing on every settings page, above the headings they switched between.
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: "0.01em",
            minHeight: 44,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: darkMode ? "#A7CDB5" : "#0B4DA2",
            fontWeight: 600,
            letterSpacing: "0.02em",
          },
        },
      },
      // The outline's notch is sized by a hidden copy of the label inside its
      // <legend>. The label is drawn at weight 600 with 0.02em tracking but the
      // legend kept the default weight, so every long label was wider than its
      // notch and the border ran through its tail - most visibly in Greek,
      // where labels are longer ("Ετήσια αναπροσαρμογή τιμής λιανικής (%)").
      // The legend now carries the label's own weight and tracking.
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            "& legend": {
              fontWeight: 600,
              letterSpacing: "0.02em",
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: darkMode ? "#A7CDB5" : "#0B4DA2",
            fontWeight: 600,
            letterSpacing: "0.02em",
            "&.Mui-focused": {
              color: darkMode ? "#A7CDB5" : "#0B4DA2",
            },
          },
        },
      },
    },
  });
